import { useEffect, useState } from 'react';
import type { OrderHistoryResponse, OrderItem } from '../../types/member.js';

/**
 * `mart__orders.order_status`（Phoenix 体系）の CANCELED。
 * API が `isCanceled` を返さない旧版だったとき用のフォールバック判定にのみ使う。
 * API 側 `shared/order-status/order-status-labels.ts` の同名定数と二重管理になるため、
 * デプロイ skew 期間が過ぎたら `isCanceled` 必須化とあわせて削除してよい。
 *
 * @graph-connects none
 */
const PHOENIX_ORDER_STATUS_CANCELED = 15;

interface OrderTabProps {
  data: OrderHistoryResponse;
}

/** @graph-connects none */
export function OrderTab({ data }: OrderTabProps) {
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null);

  useEffect(() => {
    if (!enlargedImage) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEnlargedImage(null);
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [enlargedImage]);

  if (data.orders.length === 0) {
    return (
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] py-12 text-center text-sm text-[var(--color-text-muted)]">
        オーダー履歴はありません
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3">
        {data.orders.map((order) => (
          <OrderCard
            key={order.phoenixOrderId}
            order={order}
            onImageClick={(url) => {
              setEnlargedImage(url);
            }}
          />
        ))}
      </div>
      {enlargedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={() => {
            setEnlargedImage(null);
          }}
        >
          <img
            src={enlargedImage}
            alt="拡大画像"
            className="max-h-[85vh] max-w-[90vw] rounded-lg shadow-xl"
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
          <button
            className="absolute right-4 top-4 rounded-full bg-white/20 px-3 py-1 text-sm text-white backdrop-blur hover:bg-white/30"
            onClick={() => {
              setEnlargedImage(null);
            }}
          >
            ✕ 閉じる
          </button>
        </div>
      )}
    </>
  );
}

/** @graph-connects none */
function OrderCard({
  order,
  onImageClick,
}: {
  order: OrderItem;
  onImageClick: (url: string) => void;
}) {
  // 見た目のデエンファシスは「キャンセル全般」、再選定の文言は「再選定先が実在するもの」だけ。
  // Phoenix の CANCELED には通常キャンセルも含まれるので、この2つを同じ条件で扱わない。
  // isCanceled は表示ラベルと同じ入力から API が導くため、文言と見た目が食い違わない。
  // API 先行ロールバック時に undefined になりうるので Phoenix 生値でフォールバックする。
  const isCanceled = order.isCanceled ?? order.orderStatus === PHOENIX_ORDER_STATUS_CANCELED;
  const isCanceledForRestyling = order.restyledOrderId !== null;
  const statusLabel = order.orderStatusLabel;

  return (
    <div
      className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)]"
      style={{
        borderLeftWidth: order.isRestyled ? '3px' : undefined,
        borderLeftColor: order.isRestyled ? 'var(--color-warning)' : undefined,
        opacity: isCanceled ? 0.75 : 1,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-[13px] font-semibold">
            #{String(order.phoenixOrderId)}
          </span>
          <span className="text-[12px] text-[var(--color-text-secondary)]">
            {formatDate(order.createdAt)}
          </span>
          <span
            className="inline-block rounded-full px-2 py-0.5 text-[11px] font-medium"
            style={{
              background: isCanceled ? 'var(--color-error-bg)' : 'var(--color-info-bg)',
              color: isCanceled ? 'var(--color-error)' : 'var(--color-info)',
            }}
            // 配送・返却セクションは Phoenix 生値を出すため、突き合わせ用に残す
            title={`Phoenix order_status: ${String(order.orderStatus)}`}
          >
            {statusLabel}
          </span>
          {order.isRestyled && (
            <span
              className="inline-block rounded-full px-2 py-0.5 text-[11px] font-medium"
              style={{ background: 'var(--color-warning-bg)', color: 'var(--color-warning)' }}
            >
              再選定
            </span>
          )}
          {order.originalOrderId !== null && (
            <span className="text-[11px] text-[var(--color-text-muted)]">
              元オーダー: #{String(order.originalOrderId)}
            </span>
          )}
          {order.restyledOrderId !== null && (
            <span className="text-[11px] text-[var(--color-text-muted)]">
              再選定先: #{String(order.restyledOrderId)}
            </span>
          )}
        </div>
        {order.stylistName && (
          <span className="text-[12px] text-[var(--color-text-secondary)]">
            スタイリスト: {order.stylistName}
          </span>
        )}
      </div>

      <div className="p-4">
        {/* Reject reason callout */}
        {order.isRestyled && order.rejectReason && (
          <div
            className="mb-3.5 rounded-md border p-3"
            style={{
              background: 'var(--color-warning-bg)',
              borderColor: 'color-mix(in srgb, var(--color-warning) 30%, transparent)',
            }}
          >
            <div
              className="mb-1 text-[11px] font-semibold tracking-wide"
              style={{ color: 'var(--color-warning)' }}
            >
              再選定理由（お客様フィードバック）
            </div>
            <div className="text-[13px] leading-relaxed text-[var(--color-text-primary)]">
              {order.rejectReason}
            </div>
          </div>
        )}

        {isCanceledForRestyling && (
          <div className="mb-3.5 text-[12px] text-[var(--color-text-muted)]">
            再選定後のオーダーが作成されています
          </div>
        )}

        {/* SKUs with item ID, self-select, coordinate numbers */}
        <Section
          label={isCanceledForRestyling ? 'アイテム一覧（元のスタイリング）' : 'アイテム一覧'}
        >
          {order.skus.length > 0 ? (
            <div className="flex flex-wrap gap-2.5">
              {order.skus.map((sku) => (
                <div key={sku.skuId} className="flex w-[80px] flex-col items-center gap-1">
                  {sku.imageUrl ? (
                    <button
                      type="button"
                      className="cursor-zoom-in border-0 bg-transparent p-0"
                      onClick={() => {
                        onImageClick(sku.imageUrl ?? '');
                      }}
                    >
                      <img
                        src={sku.imageUrl}
                        alt={`SKU ${String(sku.skuId)}`}
                        className="h-[80px] w-[64px] rounded border border-[var(--color-border)] object-cover transition-transform hover:scale-105"
                      />
                    </button>
                  ) : (
                    <div className="h-[80px] w-[64px] rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)]" />
                  )}
                  <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                    {String(sku.skuId)}
                  </span>
                  {sku.itemId !== null && (
                    <span className="font-mono text-[9px] text-[var(--color-text-muted)]">
                      個品: {String(sku.itemId)}
                    </span>
                  )}
                  {sku.isUserPicked && (
                    <span
                      className="inline-block rounded-full px-1.5 py-px text-[9px] font-semibold"
                      style={{
                        background: 'var(--color-accent-bg)',
                        color: 'var(--color-accent-primary)',
                      }}
                    >
                      セルフ
                    </span>
                  )}
                  {sku.coordinateNumbers.length > 0 && (
                    <span className="text-[9px] text-[var(--color-text-muted)]">
                      コーデ {sku.coordinateNumbers.join(',')}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <span className="text-[12px] text-[var(--color-text-muted)]">
              スタイリング中のため未確定
            </span>
          )}
        </Section>

        {/* Coordinate patterns */}
        {order.coordinatePatterns.length > 0 && (
          <Section label="コーデ組み合わせ">
            {order.coordinatePatterns.some((cp) => cp.generationStatus !== 'COMPLETED') && (
              <div
                className="mb-2 rounded-md border p-2.5 text-[12px]"
                style={{
                  background: 'var(--color-warning-bg)',
                  borderColor: 'color-mix(in srgb, var(--color-warning) 30%, transparent)',
                  color: 'var(--color-warning)',
                }}
              >
                一部のコーデ画像が生成失敗／未生成です。お客様画面では番号が詰められて表示されています。
              </div>
            )}
            <div className="flex flex-col gap-2">
              {order.coordinatePatterns.map((cp) => (
                <div
                  key={`${String(cp.coordinateNumber)}-${cp.generationStatus}`}
                  className="flex items-start gap-3 rounded-md border p-2.5"
                  style={{
                    borderColor:
                      cp.generationStatus !== 'COMPLETED'
                        ? 'color-mix(in srgb, var(--color-error) 40%, transparent)'
                        : 'var(--color-border)',
                    background:
                      cp.generationStatus !== 'COMPLETED'
                        ? 'var(--color-error-bg)'
                        : 'var(--color-bg-elevated)',
                  }}
                >
                  {cp.generationStatus === 'COMPLETED' && cp.previewImageUrl ? (
                    <button
                      type="button"
                      className="cursor-zoom-in border-0 bg-transparent p-0"
                      onClick={() => {
                        onImageClick(cp.previewImageUrl ?? '');
                      }}
                    >
                      <img
                        src={cp.previewImageUrl}
                        alt={`コーデ ${String(cp.coordinateNumber)}`}
                        className="h-[100px] w-[100px] shrink-0 rounded border border-[var(--color-border)] object-cover transition-transform hover:scale-105"
                      />
                    </button>
                  ) : (
                    <div className="flex h-[100px] w-[100px] shrink-0 flex-col items-center justify-center rounded border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[10px] text-[var(--color-text-muted)]">
                      {cp.generationStatus === 'COMPLETED'
                        ? '画像なし'
                        : cp.generationStatus === 'FAILED'
                          ? '生成失敗'
                          : '未生成'}
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[12px] font-semibold">
                        コーデ {String(cp.coordinateNumber)}
                      </span>
                      {cp.generationStatus !== 'COMPLETED' && (
                        <span
                          className="inline-block rounded-full px-2 py-0.5 text-[10px] font-medium"
                          style={{
                            background: 'var(--color-error-bg)',
                            color: 'var(--color-error)',
                          }}
                        >
                          {cp.generationStatus === 'FAILED' ? '画像生成失敗' : '画像未生成'}
                        </span>
                      )}
                      {cp.generationStatus === 'COMPLETED' && !cp.isNewlyGenerated && (
                        <span
                          className="inline-block rounded-full px-1.5 py-0.5 text-[10px]"
                          style={{
                            background: 'var(--color-bg-surface)',
                            color: 'var(--color-text-muted)',
                          }}
                        >
                          過去画像
                        </span>
                      )}
                    </div>
                    {cp.customerFacingNumber !== null && (
                      <span className="text-[11px] text-[var(--color-text-muted)]">
                        お客様表示: コーデ {String(cp.customerFacingNumber)}
                      </span>
                    )}
                    {cp.fkuCodes.length > 0 && (
                      <span className="text-[11px] text-[var(--color-text-secondary)]">
                        {cp.fkuCodes.join(' × ')}
                      </span>
                    )}
                    {cp.skuIds.length > 0 && (
                      <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                        SKU: {cp.skuIds.map(String).join(', ')}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Customer request */}
        {order.stylingRequest && (
          <Section label="お客様リクエスト">
            <p className="text-[13px] leading-relaxed text-[var(--color-text-secondary)]">
              {order.stylingRequest}
            </p>
            {order.userSceneLabels.length > 0 && (
              <div className="mt-1 flex gap-1">
                {order.userSceneLabels.map((scene) => (
                  <span
                    key={scene}
                    className="inline-block rounded-full px-2 py-0.5 text-[10px] font-medium"
                    style={{
                      background: 'var(--color-accent-bg)',
                      color: 'var(--color-accent-primary)',
                    }}
                  >
                    {scene}
                  </span>
                ))}
              </div>
            )}
          </Section>
        )}

        {/* Stylist comment */}
        {order.stylistComment && (
          <Section label="スタイリストコメント">
            <p className="line-clamp-3 text-[13px] leading-relaxed text-[var(--color-text-secondary)]">
              {order.stylistComment}
            </p>
          </Section>
        )}

        {/* Karte link */}
        <div className="mt-3">
          <a
            href={order.karteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded border border-[var(--color-border)] px-3 py-1.5 text-[12px] font-medium text-[var(--color-accent-primary)] transition-colors hover:bg-[var(--color-accent-bg)]"
          >
            カルテを開く &#x2197;
          </a>
        </div>

        {/* Delivery */}
        {order.delivery && (
          <SubSection title="配送">
            <KvGrid>
              <Kv label="配送ID" value={String(order.delivery.deliveryOrderId)} mono />
              <Kv label="倉庫連携日" value={formatDate(order.delivery.cooperatedDate)} />
              <Kv label="倉庫作業予定日" value={formatDate(order.delivery.scheduleDate)} />
              <Kv label="お届け予定日" value={formatDate(order.delivery.deliverySchedule)} />
              <Kv label="配送業者" value={order.delivery.deliveryProvider} />
              <Kv label="問い合わせ番号" value={order.delivery.deliveryNumber} mono />
              <Kv
                label="ステータス"
                value={
                  order.delivery.orderStatus !== null ? String(order.delivery.orderStatus) : null
                }
              />
            </KvGrid>
            {order.delivery.deliveryAddress && (
              <div className="mt-2">
                <div className="text-[11px] text-[var(--color-text-muted)]">お届け先</div>
                <div className="mt-0.5 text-[12px] font-medium">
                  {formatAddress(order.delivery.deliveryAddress)}
                </div>
              </div>
            )}
          </SubSection>
        )}

        {/* Sendback */}
        {order.sendback && (
          <SubSection title="返却">
            <KvGrid>
              <Kv label="返却ID" value={String(order.sendback.sendbackOrderId)} mono />
              <Kv label="返却手続き日" value={formatDate(order.sendback.requestDate)} />
              <Kv label="返却完了日" value={formatDate(order.sendback.returnedDate)} />
              <Kv label="返却方法" value={order.sendback.sendbackMethod} />
              <Kv label="問い合わせ番号" value={order.sendback.deliveryNumber} mono />
              <Kv
                label="ステータス"
                value={
                  order.sendback.orderStatus !== null ? String(order.sendback.orderStatus) : null
                }
              />
            </KvGrid>
          </SubSection>
        )}
      </div>
    </div>
  );
}

/** @graph-connects none */
function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3.5">
      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
        {label}
      </div>
      {children}
    </div>
  );
}

/** @graph-connects none */
function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-2.5 rounded-md bg-[var(--color-bg-elevated)] p-3">
      <div className="mb-2 text-[11px] font-semibold text-[var(--color-text-secondary)]">
        {title}
      </div>
      {children}
    </div>
  );
}

/** @graph-connects none */
function KvGrid({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-2">{children}</div>
  );
}

/** @graph-connects none */
function Kv({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <div>
      <div className="text-[11px] text-[var(--color-text-muted)]">{label}</div>
      <div
        className={`text-[12px] font-medium ${mono ? 'font-mono' : ''} ${!value ? 'text-[var(--color-text-muted)]' : ''}`}
      >
        {value ?? '-'}
      </div>
    </div>
  );
}

/** @graph-connects none */
function formatDate(isoDate: string | null | undefined): string {
  if (!isoDate) return '-';
  const _d = new Date(isoDate);
  if (Number.isNaN(_d.getTime())) return isoDate;
  return _d.toLocaleDateString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/** @graph-connects none */
function addrStr(val: unknown): string {
  return typeof val === 'string' ? val : '';
}

/** @graph-connects none */
function formatAddress(addr: Record<string, unknown>): string {
  const postal = addrStr(addr['postal_code']);
  const parts = [
    postal ? `〒${postal}` : '',
    addrStr(addr['prefecture']),
    addrStr(addr['city']),
    addrStr(addr['street']),
    addrStr(addr['building']),
  ].filter(Boolean);
  return parts.join(' ') || '-';
}
